import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ProductLicenseManagementState from "interfaces/state/ProductLicenseManagementState";
import {
  getProductLicenseAsync,
  getAutoPurchasableProductLicenseAsync,
  createProductLicenseAsync, 
  updateProductLicenseAsync, 
  deleteProductLicenseAsync } from "apis/ProductLicenseApi";
import LicenseRequest from "interfaces/request/LicenseRequest";
import { toastSuccess } from "components/common/ToastNotification";
import { RootState } from "store/store";
import { getBillingIntervalDropdownValues, getActionValidationDurationUnitDropdownValues } from "./DropdownValueSlice";
import AdminProductLicense from "interfaces/response/AdminProductLicense";

const initialState: ProductLicenseManagementState = {
  loading: false,
  productLicense: [],
  autoPurchasableProductLicense: []
};

const updateLicenseInState = (
  LicenseToBeUpdated: number,
  LicenseDetails: AdminProductLicense,
  state: ProductLicenseManagementState
) => {
  const existingLicenseIndex = state.productLicense.findIndex(
    (license: AdminProductLicense) => license.id === LicenseToBeUpdated
  );
  if (existingLicenseIndex > -1) {
    state.productLicense[existingLicenseIndex] = LicenseDetails;
  }
};

const deleteLicenseFromState = (licenceId: number, state: ProductLicenseManagementState) => {
  const existingLicenseIndex = state.productLicense.findIndex(
    (license: AdminProductLicense) => license.id === licenceId
  );
  if (existingLicenseIndex > -1) {
    state.productLicense.splice(existingLicenseIndex, 1);
  }
}

export const loadLicenseRelatedDropdownData = createAsyncThunk<
  any,
  void,
  { state: RootState }
>("productLicenseManagement/loadLicenseRelatedDropdownData", async (_, thunkAPI) => {

  const billingIntervalDropdownValues = thunkAPI.dispatch(getBillingIntervalDropdownValues());
  const actionValidationDurationUnitDropdownValues = thunkAPI.dispatch(getActionValidationDurationUnitDropdownValues());

  return Promise.all([billingIntervalDropdownValues, actionValidationDurationUnitDropdownValues]);
});

export const getProductLicense = createAsyncThunk(
  "productLicenseManagement/getProductLicense",
  async (productId: number) => {
    return await getProductLicenseAsync(productId);
  }
);

export const getAutoPurchasableProductLicense = createAsyncThunk(
  "productLicenseManagement/getAutoPurchasableProductLicense",
  async (productId: number) => {
    return await getAutoPurchasableProductLicenseAsync(productId);
  }
);

export const createNewProductLicense = createAsyncThunk(
  "productLicenseManagement/createNewProductLicense",
  async (postData: LicenseRequest) => {
    return await createProductLicenseAsync(postData);
  }
);

export const updateProductLicense = createAsyncThunk<
  AdminProductLicense,
  { licenseId: number; licenseRequest: LicenseRequest }
>(
  "productLicenseManagement/updateProductLicense", async (request) => {
  return await updateProductLicenseAsync(request.licenseId, request.licenseRequest);
});

export const deleteProductLicense = createAsyncThunk<any, {licenseId: number}, { state: RootState }>(
  "productLicenseManagement/deleteProductLicense", async (request) => {
    await deleteProductLicenseAsync(request.licenseId);
  }
);

export const productLicenseManagementSlice = createSlice({
  name: "productLicenseManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getProductLicense.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getProductLicense.fulfilled, (state, action) => {
      state.loading = false;
      state.productLicense = action.payload;
    });
    builder.addCase(getProductLicense.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getAutoPurchasableProductLicense.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAutoPurchasableProductLicense.fulfilled, (state, action) => {
      state.autoPurchasableProductLicense = action.payload;
      state.loading = false;
    });
    builder.addCase(getAutoPurchasableProductLicense.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(createNewProductLicense.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createNewProductLicense.fulfilled, (state, action) => {
      state.loading = false;
      state.productLicense.unshift(action.payload);
    });
    builder.addCase(createNewProductLicense.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateProductLicense.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateProductLicense.fulfilled, (state, action) => {
      updateLicenseInState(action.payload.id, action.payload, state);
      toastSuccess("License details updated.");
      state.loading = false;
    });
    builder.addCase(updateProductLicense.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteProductLicense.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteProductLicense.fulfilled, (state, action) => {
      deleteLicenseFromState(action.meta.arg.licenseId, state);
      toastSuccess("License deleted.");
      state.loading = false;
    });
    builder.addCase(deleteProductLicense.rejected, (state) => {
      state.loading = false;
    });
  },
});

export default productLicenseManagementSlice.reducer;
